import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Check, AlertTriangle, Trash2 } from "lucide-react";

const RULES = [
  "Data only from public/verified sources (Companies House, verified enrichment) — never invented",
  "Limited companies / PLC / LLP: B2B email allowed with opt-out (legitimate interests)",
  "Sole traders & individuals: cold email held for consent or routed to another channel",
  "No marketing email at all until a privacy policy URL is set",
  "Opt-outs honoured immediately + kept on a permanent suppression list",
  "Max one cold contact per prospect per 14 days",
];

export const CompliancePolicy = () => {
  const [p, setP] = useState(null);
  const [review, setReview] = useState(null);

  const load = async () => {
    const [pol, rev] = await Promise.all([api.compliancePolicy(), api.retentionReview().catch(() => null)]);
    setP(pol); setReview(rev);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const saved = await api.updateCompliancePolicy({
      privacy_policy_url: p.privacy_policy_url, terms_url: p.terms_url,
      data_contact_email: p.data_contact_email, retention_months: Number(p.retention_months),
      outreach_policy: p.outreach_policy,
    });
    setP(saved); toast.success("Compliance policy saved."); load();
  };
  const purge = async () => { const r = await api.retentionPurge(); toast.success(`Purged ${r.purged} stale prospects.`); load(); };

  if (!p) return null;
  const field = (k, v) => setP({ ...p, [k]: v });

  return (
    <div data-testid="compliance-policy" className="rounded-2xl border border-white/10 bg-[#0F1420] p-4">
      <h3 className="kz-head font-bold mb-1 flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-400" /> Privacy & outreach compliance</h3>
      <p className="text-xs text-slate-500 mb-4">UK PECR/GDPR rules KURT enforces before any outreach. Enforced in code, not left to judgement.</p>

      <div className={`rounded-lg px-3 py-2 mb-4 text-xs flex items-center gap-2 ${p.cold_email_enabled ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>
        {p.cold_email_enabled ? <Check size={14} /> : <AlertTriangle size={14} />}
        {p.cold_email_enabled ? "Cold B2B email is enabled (privacy policy set)." : "Cold email is BLOCKED until a privacy policy URL is set."}
      </div>

      <div className="space-y-1.5 mb-4">
        {RULES.map((r, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-slate-400"><Check size={12} className="text-emerald-400 mt-0.5 shrink-0" /> {r}</div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-xs text-slate-400 mb-1">Privacy policy URL</div>
          <input data-testid="privacy-url-input" value={p.privacy_policy_url || ""} onChange={(e) => field("privacy_policy_url", e.target.value)}
            placeholder="https://smemagazine.co.uk/privacy" className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-emerald-500/40" />
        </div>
        <div>
          <div className="text-xs text-slate-400 mb-1">Terms URL (optional)</div>
          <input data-testid="terms-url-input" value={p.terms_url || ""} onChange={(e) => field("terms_url", e.target.value)}
            placeholder="(optional)" className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-emerald-500/40" />
        </div>
        <div>
          <div className="text-xs text-slate-400 mb-1">Data contact email</div>
          <input data-testid="data-contact-input" value={p.data_contact_email || ""} onChange={(e) => field("data_contact_email", e.target.value)}
            className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-emerald-500/40" />
        </div>
        <div>
          <div className="text-xs text-slate-400 mb-1">Retention (months)</div>
          <input data-testid="retention-input" type="number" min="1" value={p.retention_months} onChange={(e) => field("retention_months", e.target.value)}
            className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-emerald-500/40" />
        </div>
      </div>

      <div className="mb-3">
        <div className="text-xs text-slate-400 mb-1">Internal outreach policy</div>
        <textarea data-testid="outreach-policy" value={p.outreach_policy || ""} onChange={(e) => field("outreach_policy", e.target.value)} rows={7}
          className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-2 text-xs text-slate-300 outline-none focus:border-emerald-500/40 resize-y leading-relaxed" />
      </div>

      <div className="flex items-center gap-2">
        <button data-testid="compliance-save" onClick={save} className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 transition-all">Save policy</button>
        {review && (
          <div className="flex items-center gap-2 ml-auto text-[11px] text-slate-400">
            <span>{review.stale_prospects} prospects past {review.retention_months}m</span>
            {review.stale_prospects > 0 && (
              <button data-testid="retention-purge" onClick={purge} className="flex items-center gap-1 font-semibold px-2.5 py-1.5 rounded-lg border border-red-500/25 text-red-300 hover:bg-red-500/10"><Trash2 size={12} /> Purge</button>
            )}
          </div>
        )}
      </div>
      <p className="text-[10px] text-slate-600 mt-2">Not legal advice — mirror this with the public privacy policy on smemagazine.co.uk and have a solicitor review.</p>
    </div>
  );
};
